/**
 * Normalizes the mandatory flag coming from the backend.
 * Handles boolean true/false as well as string/number variants
 * ("true"/"false", 1/0) so an inconsistent API response never
 * silently flips a field's required state.
 * @param {Object} field - The dynamic field configuration
 * @returns {boolean}
 */
export const isFieldMandatory = (field) => {
  const mandatory = field?.mandatory;
  if (typeof mandatory === "string") {
    return mandatory.toLowerCase() === "true" || mandatory === "1";
  }
  return Boolean(mandatory);
};

/**
 * Validates a dynamic field value based on its configuration
 * @param {Object} field - The dynamic field configuration
 * @param {string|number|boolean} value - The value to validate
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
export const validateDynamicField = (field, value, t) => {
  const mandatory = isFieldMandatory(field);

  // Type 0: Text
  if (field.type === 0) {
    const isEmpty = value === null || value === undefined || value.trim() === '';

    // Check mandatory
    if (mandatory && isEmpty) {
      return t("This field is required");
    }
    
    // Empty is OK if not mandatory (field was never touched or was cleared)
    if (isEmpty) return null;
    
    // Check max length
    if (value.length > 1000) {
      return t("Max 1000 characters");
    }
    
    // Check restricted characters (same as notes field)
    const restrictedCharsRegex = /[<>`|~^]/;
    if (restrictedCharsRegex.test(value)) {
      return t("Characters < > ` | ~ ^ are not allowed");
    }
    
    return null;
  }

  // Type 1: Int
  if (field.type === 1) {
    const isEmpty = value === '' || value === null || value === undefined;

    // Check mandatory
    if (mandatory && isEmpty) {
      return t("This field is required");
    }
    
    // Empty is OK if not mandatory (field was never touched or was cleared)
    if (isEmpty) return null;
    
    const num = Number(value);
    
    // Check if valid number
    if (isNaN(num)) {
      return t("Must be a valid number");
    }
    
    // Check if integer
    if (!Number.isInteger(num)) {
      return t("Must be a whole number");
    }
    
    // Check min value
    if (field.minValue != null && num < field.minValue) {
      return t("Minimum value is {{value}}", { value: field.minValue });
    }
    
    // Check max value
    if (field.maxValue != null && num > field.maxValue) {
      return t("Maximum value is {{value}}", { value: field.maxValue });
    }
    
    // Check step
    if (field.step != null && field.minValue != null) {
      const offset = num - field.minValue;
      if (offset % field.step !== 0) {
        return t("Value must be in increments of {{value}}", { value: field.step });
      }
    }
    
    return null;
  }

  // Type 2: Dec
  if (field.type === 2) {
    const isEmpty = value === '' || value === null || value === undefined;

    // Check mandatory
    if (mandatory && isEmpty) {
      return t("This field is required");
    }
    
    // Empty is OK if not mandatory (field was never touched or was cleared)
    if (isEmpty) return null;
    
    const num = Number(value);
    
    // Check if valid number
    if (isNaN(num)) {
      return t("Must be a valid number");
    }
    
    // Check min value
    if (field.minValue != null && num < field.minValue) {
      return t("Minimum value is {{value}}", { value: field.minValue });
    }
    
    // Check max value
    if (field.maxValue != null && num > field.maxValue) {
      return t("Maximum value is {{value}}", { value: field.maxValue });
    }
    
    // Check step (with epsilon for floating point comparison)
    if (field.step != null && field.minValue != null) {
      const offset = num - field.minValue;
      const remainder = Math.abs(offset % field.step);
      const epsilon = 0.0001;
      
      if (remainder > epsilon && Math.abs(remainder - field.step) > epsilon) {
        return t("Value must be in increments of {{value}}", { value: field.step });
      }
    }
    
    return null;
  }

  // Type 3: Boolean (Bit switch - defaults to "false"/No)
  if (field.type === 3) {
    // Untouched switch is treated as "false" (No), which satisfies mandatory
    return null;
  }

  return null;
};

/**
 * Validates all dynamic fields
 * @param {Array} dynamicFields - Array of dynamic field configurations
 * @param {Object} values - Object with field values keyed by dynamicFieldId
 * @param {Function} t - Translation function
 * @returns {Object} - Object with errors keyed by dynamicFieldId
 */
export const validateAllDynamicFields = (dynamicFields, values, t) => {
  const errors = {};
  
  if (!dynamicFields || dynamicFields.length === 0) {
    return errors;
  }
  
  dynamicFields.forEach((field) => {
    const value = field.type === 3
      ? (values[field.dynamicFieldId] ?? 'false')
      : values[field.dynamicFieldId];
    const error = validateDynamicField(field, value, t);
    if (error) {
      errors[field.dynamicFieldId] = error;
    }
  });
  
  return errors;
};

/**
 * Checks if all dynamic fields are valid
 * @param {Array} dynamicFields - Array of dynamic field configurations
 * @param {Object} values - Object with field values keyed by dynamicFieldId
 * @param {Function} t - Translation function
 * @returns {boolean} - True if all fields are valid
 */
export const areAllDynamicFieldsValid = (dynamicFields, values, t) => {
  if (!dynamicFields || dynamicFields.length === 0) {
    return true;
  }
  
  return dynamicFields.every((field) => {
    // Boolean switches default to "false" when never touched
    const value = field.type === 3
      ? (values[field.dynamicFieldId] ?? 'false')
      : values[field.dynamicFieldId];
    const error = validateDynamicField(field, value, t);
    return error === null;
  });
};
